/* eslint-disable react-hooks/exhaustive-deps */
import IconDelete from "@mui/icons-material/Delete";
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "validate.js";
import {
  fetchComments,
  resetStateComments,
} from "../../app/slices/commentSlice";
import { showSnackbar } from "../../app/slices/commonSlice";
import {
  addReport,
  fetchReports,
  resetStateReports,
} from "../../app/slices/reportSlice";
import {
  dateFormat,
  dateFormatWithTime,
  hasAccess,
  oracleDateFormat,
  applicationStatus as statuses,
} from "../../app/utils";
import reportName from "../../assets/static/reportNames.json";
import EmptyDataContainer from "../emptyDataContainer/emptyDataContainer";
import ErrorContainer from "../errorContainer/errorContainer";
import LoadingContainer from "../loadingContainer/loadingContainer";
import "./CommentsContainer.css";

const CommentsContainer = ({
  recordId,
  errors,
  formValues,
  setFormValues,
  setErrors,
  showForm = true,
}) => {
  const {
    get: { list, status: statusGet, error: errorGet },
  } = useSelector(({ comments }) => comments);

  const {
    get: { list: reportDates, status: reportStatusGet, error: reportErrorGet },
    post: {
      data: dataPostReport,
      status: statusPostReport,
      error: errorPostReport,
    },
  } = useSelector(({ reports }) => reports);

  const [canEdit] = useState(formValues?.status === "E");
  const [applicationStatuses, setApplicationStatuses] = useState([]);
  const dispatch = useDispatch();

  //* use effects
  useEffect(() => {
    /* set application statuses to show */
    let _statuses = [];
    switch (formValues?.status) {
      case "S":
        statuses.forEach((status) => {
          if (
            status.value === "E" ||
            status.value === "X" ||
            status.value === "R"
          ) {
            _statuses.push(status);
          }
        });
        break;
      case "E":
        statuses.forEach((status) => {
          if (status.value === "T" || status.value === "N") {
            _statuses.push(status);
          }
        });
        break;
      case "T":
        statuses.forEach((status) => {
          if (status.value === "P" || status.value === "F") {
            _statuses.push(status);
          }
        });
        break;
      case "P":
        statuses.forEach((status) => {
          if (status.value === "C") {
            _statuses.push(status);
          }
        });
        break;
      default:
        break;
    }
    setApplicationStatuses(_statuses);
  }, [formValues?.status]);

  useEffect(() => {
    loadComments();
    if (showForm) {
      loadReportDates();
    }
    dispatch(resetStateComments());
    dispatch(resetStateReports());

    return () => {
      dispatch(resetStateComments());
      dispatch(resetStateReports());
    };
  }, [recordId]);

  useEffect(() => {
    if (dataPostReport) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Changes saved successfully.",
          type: "success",
        })
      );
      dispatch(resetStateReports(["post"]));
      loadReportDates();
    }
  }, [dataPostReport]);

  useEffect(() => {
    if (errorPostReport) {
      dispatch(
        showSnackbar({
          open: true,
          message: errorPostReport?.message || "An error occurred",
          type: "error",
        })
      );

      dispatch(resetStateReports(["post"]));
      dispatch(resetStateReports(["get"]));
      setTimeout(() => {
        loadReportDates();
      }, 250);
    }
  }, [errorPostReport]);

  useEffect(() => {
    setFormValues({
      ...formValues,
      comment: null,
    });
  }, [formValues?.documentType]);

  //* methods

  const loadComments = () => {
    dispatch(
      fetchComments({
        RECORD_ID: recordId,
      })
    );
  };

  const loadReportDates = () => {
    dispatch(fetchReports({ APPLICATION_ID: formValues?.applicationId }));
  };

  const BuildCommentView = () => {
    return (
      <Grid
        sm={12}
        md={showForm ? 6 : 12}
        className="comment-container"
        style={{ padding: "10px" }}
      >
        {statusGet === "loading" && (
          <LoadingContainer
            hasBackground={false}
            message="Loading comments..."
            id="loading-container-comments"
          />
        )}

        {errorGet !== null && statusGet !== "loading" && (
          <ErrorContainer
            id="error-container-comments"
            hasBackground={false}
            message={errorGet?.message || "An error occurred"}
            onReload={() => {
              if (errorGet) {
                loadComments();
              } else {
                dispatch(
                  fetchReports({ APPLICATION_ID: formValues?.applicationId })
                );
              }
            }}
          />
        )}

        {statusGet === "idle" && list?.length <= 0 && (
          <EmptyDataContainer
            message="No comments found"
            hasBackground={false}
            id="empty-data-users"
          />
        )}

        {list?.map((comment, index) => (
          <div className="flex-row" key={`comment-${index}`}>
            <div className="content mb-20">
              <div className="username">
                {comment?.EMAIL?.includes("@bot.go.tz")
                  ? "Evaluator"
                  : comment?.EMAIL}
              </div>
              <div className="mt-5"> {comment?.DATA}</div>
              <div className="mt-10 text-align-right date">
                {moment(comment?.CREATED_AT).format(dateFormatWithTime)}
              </div>
            </div>
          </div>
        ))}
      </Grid>
    );
  };

  const BuildFormView = () => {
    return (
      <Grid sm={12} md={6} className="container-form">
        <Grid
          container
          style={
            hasAccess({ type: "name", resource: "EDIT_APPLICATION" })
              ? {
                  background: "#f6f6f6",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  margin: "20px 0",
                }
              : {}
          }
        >
          {hasAccess({ type: "name", resource: "EDIT_APPLICATION" }) && (
            <>
              <Grid item xs={12} className="padded-grid-item">
                <b>Update Status</b>
              </Grid>
              <Grid item md={6} xs={12} className="padded-grid-item">
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  name="applicationStatus"
                  id="Status"
                  select
                  label="Application Status"
                  error={errors["applicationStatus"] ? true : false}
                  helperText={
                    errors["applicationStatus"]
                      ? errors.applicationStatus[0]
                      : null
                  }
                  onChange={(event) => {
                    event.preventDefault();
                    setFormValues({
                      ...formValues,
                      applicationStatus: event.target.value,
                    });
                  }}
                  value={formValues?.applicationStatus || null}
                >
                  {applicationStatuses.map((option) => {
                    return (
                      <MenuItem key={option.label} value={option.value}>
                        {option.label}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </>
          )}

          {canEdit &&
            hasAccess({ type: "name", resource: "EDIT_APPLICATION" }) && (
              <>
                <Grid item xs={12} className="padded-grid-item">
                  <b>Add Test Dates</b>
                </Grid>

                <Grid item md={6} xs={12} className="padded-grid-item">
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    name="reportName"
                    id="reportName"
                    select
                    label="Report Name"
                    error={errors["reportName"] ? true : false}
                    helperText={
                      errors["reportName"] ? errors.reportName[0] : null
                    }
                    onChange={(event) => {
                      event.preventDefault();
                      setFormValues({
                        ...formValues,
                        reportName: event.target.value,
                      });
                    }}
                    value={formValues?.reportName || null}
                  >
                    {reportName?.map((option) => {
                      return (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                <Grid item md={6} xs={12} className="padded-grid-item">
                  <TextField
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    label="Due Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formValues?.dueDate || ""}
                    error={errors["dueDate"] ? true : false}
                    helperText={errors["dueDate"] ? errors.dueDate[0] : null}
                    InputProps={{
                      inputProps: {
                        min: new Date().toISOString().split("T")[0] ?? "",
                      },
                    }}
                    onChange={(event) => {
                      setFormValues({
                        ...formValues,
                        dueDate: event.target.value,
                      });
                    }}
                  />
                </Grid>
              </>
            )}

          {canEdit &&
            hasAccess({ type: "name", resource: "EDIT_APPLICATION" }) && (
              <Grid item xs={12} className="padded-grid-item">
                <div className="flex-row justify-content-end ">
                  <Button
                    className="btn-login flex-row"
                    variant="contained"
                    onClick={handleAddRow}
                    disabled={statusPostReport === "loading"}
                    margin="dense"
                    size="medium"
                    color="secondary"
                    style={{ textTransform: "unset" }}
                  >
                    {statusPostReport === "loading" && (
                      <CircularProgress
                        size={18}
                        style={{ marginRight: "5px" }}
                      />
                    )}{" "}
                    + Add
                  </Button>
                </div>
              </Grid>
            )}
        </Grid>

        {BuildTable()}
      </Grid>
    );
  };

  const BuildTable = () => {
    return (
      <>
        {reportStatusGet === "loading" && (
          <LoadingContainer
            hasBackground={false}
            message="Loading report dates..."
            id="loading-container-comments"
          />
        )}

        {reportErrorGet !== null && reportStatusGet !== "loading" && (
          <ErrorContainer
            id="error-container-comments"
            hasBackground={false}
            message={reportErrorGet?.message || "An error occurred"}
            onReload={() => {
              dispatch(
                fetchReports({ APPLICATION_ID: formValues?.applicationId })
              );
            }}
          />
        )}

        {reportStatusGet === "idle" && reportDates?.length <= 0 && (
          <EmptyDataContainer
            message="No reports found"
            hasBackground={false}
            id="empty-data-users"
          />
        )}

        {reportDates?.length > 0 && (
          <table className="stakeholders-table">
            <thead>
              <th>Report Name</th>
              <th>Due Date</th>
              <th>Status</th>
              {canEdit && <th>Action</th>}
            </thead>
            <tbody>
              {reportDates?.length === 0 && (
                <tr key={`report-`}>
                  <td colSpan={8} className="text-align-center text-muted">
                    No test dates added.
                  </td>
                </tr>
              )}

              {reportDates?.length > 0 && (
                <>
                  {reportDates?.map((row, index) => (
                    <tr>
                      <td>{row?.REPORT_NAME}</td>
                      <td className="text-align-center">
                        {moment(row?.DUE_DATE).format(dateFormat)}
                      </td>
                      <td
                        className={`text-align-center ${
                          row?.ATTACHMENT_ID ? "text-green" : "text-red"
                        }`}
                      >
                        {row?.ATTACHMENT_ID ? "Uploaded" : "Not Uploaded"}
                      </td>
                      {canEdit && (
                        <td className="td-50 delete-icon">
                          <div className="flex-row justify-content-center">
                            <IconDelete
                              onClick={() => {
                                if (window.confirm("Remove report date?")) {
                                  handleRemoveRow(index);
                                }
                              }}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        )}
      </>
    );
  };

  const handleAddRow = () => {
    let _errors = validate(formValues, {
      reportName: {
        presence: { allowEmpty: false },
      },
      dueDate: {
        presence: { allowEmpty: false },
      },
    });

    if (_errors) {
      setErrors(_errors);
    } else {
      setErrors([]);

      let indexOf = reportDates.findIndex(
        (report) =>
          report.REPORT_NAME === formValues?.reportName &&
          moment(report.DUE_DATE).format(oracleDateFormat) ===
            moment(formValues?.dueDate).format(oracleDateFormat)
      );

      if (indexOf >= 0) {
        dispatch(
          showSnackbar({
            open: true,
            message: "Report date has already been added.",
            type: "warning",
          })
        );
        return;
      }

      dispatch(
        addReport({
          REPORTS: [
            {
              APPLICATION_ID: formValues?.applicationId,
              REPORT_NAME: formValues?.reportName,
              DUE_DATE: moment(formValues?.dueDate).format(oracleDateFormat),
            },
          ],
        })
      );
      setFormValues({
        ...formValues,
        reportName: null,
        dueDate: new Date().toISOString().split("T")[0],
      });
    }
  };

  const handleRemoveRow = (index) => {
    let _reportDates = [...formValues.reportDates];
    let removedItems = _reportDates.splice(index, 1);

    dispatch(
      addReport({
        REPORTS: [],
        REMOVED: [removedItems[0]?.REPORT_NO],
      })
    );
    setFormValues({
      ...formValues,
      reportDates: _reportDates,
    });
  };

  return (
    <div className="mt-10">
      <Grid
        container
        className="flex-row"
        style={{
          height: "450px",
        }}
      >
        {BuildCommentView()}
        {showForm && BuildFormView()}
      </Grid>
    </div>
  );
};

export default CommentsContainer;
